const browserHost = window.location.hostname;
const isLocalHost = browserHost === "localhost" || browserHost === "127.0.0.1";
const isLanIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(browserHost);

const API_URL = isLocalHost || isLanIp
  ? "http://127.0.0.1:8000"
  : "/api";
const AUTH_TOKEN_KEY = "ctrlrentacancha_token";

function normalizeAmount(value) {
  return Number(Number(value).toFixed(2));
}

function normalizeDuration(value) {
  return Number.parseInt(value, 10);
}

function getApiErrorMessage(data, fallbackMessage) {
  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (item?.msg) {
          return item.msg;
        }
        return null;
      })
      .filter(Boolean)
      .join(". ");
  }
  return fallbackMessage;
}


function getAuthHeaders() {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return token
    ? { Authorization: `Bearer ${token}` }
    : {};
}


export async function fetchDashboardData(fechaReservas) {
  const reservasQuery = fechaReservas ? `?fecha=${encodeURIComponent(fechaReservas)}` : "";
  const [reservasRes, efectivoRes, resumenRes, canchasRes] = await Promise.all([
    fetch(`${API_URL}/reservas${reservasQuery}`),
    fetch(`${API_URL}/efectivo`),
    fetch(`${API_URL}/resumen`),
    fetch(`${API_URL}/canchas`),
  ]);

  const reservasData = await reservasRes.json();
  const efectivoData = await efectivoRes.json();
  const resumenData = await resumenRes.json();
  const canchasData = await canchasRes.json();

  return {
    reservas: reservasData.reservas || [],
    movimientos: efectivoData.movimientos || [],
    efectivo: efectivoData,
    resumen: resumenData,
    canchas: canchasData.canchas || [],
    canchaItems: canchasData.items || [],
  };
}


export async function createReserva(payload) {
  const reservaPayload = {
    cliente: payload.cliente,
    cancha: payload.cancha,
    fecha: payload.fecha,
    hora: payload.hora,
    duracion_min: normalizeDuration(payload.duracionMin),
    monto: normalizeAmount(payload.monto),
    pagado: payload.pagado,
    tipo: "publica",
  };

  const response = await fetch(`${API_URL}/reservas`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(reservaPayload),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo guardar la reserva");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function createMovimiento(payload) {
  const response = await fetch(`${API_URL}/efectivo`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      ...payload,
      duracion_min: normalizeDuration(payload.duracionMin),
      monto: normalizeAmount(payload.monto),
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo guardar el movimiento");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function fetchDisponibilidad(fecha) {
  const response = await fetch(`${API_URL}/disponibilidad?fecha=${fecha}`);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || "No se pudo consultar la disponibilidad");
  }

  return data;
}


export async function fetchBloques() {
  const response = await fetch(`${API_URL}/bloques`, {
    headers: {
      ...getAuthHeaders(),
    },
  });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo consultar bloques");
    error.status = response.status;
    throw error;
  }
  return data;
}


export async function createBloque(payload) {
  const response = await fetch(`${API_URL}/bloques`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(getApiErrorMessage(data, "No se pudo crear bloque"));
    error.status = response.status;
    throw error;
  }

  return data;
}

export async function updateBloqueCancha(bloqueId, cancha) {
  const response = await fetch(`${API_URL}/bloques/${bloqueId}/cancha`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ cancha }),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo actualizar la cancha del bloque");
    error.status = response.status;
    throw error;
  }

  return data;
}

export async function deleteBloque(bloqueId) {
  const response = await fetch(`${API_URL}/bloques/${bloqueId}`, {
    method: "DELETE",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo eliminar bloque");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function cancelReserva(reservaId) {
  const response = await fetch(`${API_URL}/reservas/${reservaId}/cancelar`, {
    method: "PATCH",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo cancelar la reserva");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function deleteReserva(reservaId) {
  const response = await fetch(`${API_URL}/reservas/${reservaId}`, {
    method: "DELETE",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo eliminar la reserva");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function deleteMovimientoManual(movimientoId) {
  const response = await fetch(`${API_URL}/efectivo/${movimientoId}`, {
    method: "DELETE",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo eliminar el movimiento");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function markReservaPagada(reservaId) {
  const response = await fetch(`${API_URL}/reservas/${reservaId}/pagar`, {
    method: "PATCH",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo marcar la reserva como pagada");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function updateReserva(reservaId, payload) {
  const response = await fetch(`${API_URL}/reservas/${reservaId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      cliente: payload.cliente,
      cancha: payload.cancha,
      fecha: payload.fecha,
      hora: payload.hora,
      duracion_min: normalizeDuration(payload.duracionMin),
      monto: normalizeAmount(payload.monto),
      pagado: payload.pagado,
      tipo: "publica",
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo actualizar la reserva");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function createCancha(payload) {
  const response = await fetch(`${API_URL}/canchas`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      ...payload,
      valor_hora: normalizeAmount(payload.valor_hora),
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo guardar la cancha");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function updateCancha(canchaId, payload, force = false) {
  const response = await fetch(`${API_URL}/canchas/${canchaId}?force=${force}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      ...payload,
      valor_hora: normalizeAmount(payload.valor_hora),
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo actualizar la cancha");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function deleteCancha(canchaId, force = false) {
  const response = await fetch(`${API_URL}/canchas/${canchaId}?force=${force}`, {
    method: "DELETE",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo eliminar la cancha");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function fetchReportes(fechaInicio, fechaFin) {
  const response = await fetch(`${API_URL}/reportes?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo consultar el reporte");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function fetchCierreDiario(fecha) {
  const query = fecha ? `?fecha=${encodeURIComponent(fecha)}` : "";
  const response = await fetch(`${API_URL}/cierres/diario${query}`);
  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo consultar el cierre diario");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function closeCierreDiario(fecha) {
  const query = fecha ? `?fecha=${encodeURIComponent(fecha)}` : "";
  const response = await fetch(`${API_URL}/cierres/diario${query}`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo cerrar el día");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function login(payload) {
  const response = await fetch(`${API_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo iniciar sesión");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function fetchMe(token) {
  const response = await fetch(`${API_URL}/auth/me`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "Sesión inválida");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function logout(token) {
  const response = await fetch(`${API_URL}/auth/logout`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo cerrar sesión");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function fetchUsuarios() {
  const response = await fetch(`${API_URL}/usuarios`, {
    method: "GET",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo cargar usuarios");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function createUsuario(payload) {
  const response = await fetch(`${API_URL}/usuarios`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo crear usuario");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function updateUsuarioEstado(userId, activo) {
  const response = await fetch(`${API_URL}/usuarios/${userId}/estado`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ activo }),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo actualizar estado");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function updateUsuarioPassword(userId, password) {
  const response = await fetch(`${API_URL}/usuarios/${userId}/password`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ password }),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo actualizar contraseña");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function updateUsuarioRol(userId, rol) {
  const response = await fetch(`${API_URL}/usuarios/${userId}/rol`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ rol }),
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo actualizar el rol");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function deleteUsuario(userId) {
  const response = await fetch(`${API_URL}/usuarios/${userId}`, {
    method: "DELETE",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo eliminar el usuario");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function fetchCierreDetalle(fecha) {
  const query = fecha ? `?fecha=${encodeURIComponent(fecha)}` : "";
  const response = await fetch(`${API_URL}/cierres/diario/detalle${query}`, {
    headers: { ...getAuthHeaders() },
  });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.detail || "No se pudo obtener el detalle del cierre");
    error.status = response.status;
    throw error;
  }
  return data;
}